import "./ReferralProgram.scss";
import MainTitle from "../../components/ui/MainTitle/MainTitle";
import { useTranslation } from "react-i18next";
import { useMemo, useState } from "react";
import ReferralSettingsPopup from "./ReferralSettingsPopup/ReferralSettingsPopup";
import { Table, TableProps } from "antd";
import { IReferralMember, ReferralMemberEnum } from "../../models/ReferralMember";
import dayjs from "dayjs";
import { useGetReferralMembersQuery } from "../../api/queries";
import { useGetReferralSettingsQuery } from "../../api/queries";

type ColumnsType<T> = TableProps<T>["columns"];

const ReferralProgram = () => {
  const { t } = useTranslation();

  const [showReferralSettingsPopup, setShowReferralSettingsPopup] = useState<boolean>(false);
  const [tableParams, setTableParams] = useState<{ current: number; pageSize: number }>({
    current: 1,
    pageSize: 10,
  });
  const { data = { members: [], membersCount: 0 }, isLoading } = useGetReferralMembersQuery({
    currentPage: tableParams.current,
    pageSize: tableParams.pageSize,
  });
  const { data: referralSettings = null, refetch } = useGetReferralSettingsQuery();

  const totalCount = useMemo(() => {
    if (!data) return 0;
    return data.membersCount;
  }, [data]);

  const closeSettingsPopup = () => {
    setShowReferralSettingsPopup(false);
  };

  const handleTableChange: TableProps<IReferralMember>["onChange"] = (
    pagination,
    filters,
    sorter
  ) => {
    setTableParams({
      current: pagination.current || 1,
      pageSize: pagination.pageSize || 10,
    });
  };

  const columns: ColumnsType<IReferralMember> = [
    {
      title: t("ReferrerName"),
      render: (data: IReferralMember) => {
        return data.referralToken.user.firstname + " " + data.referralToken.user.lastname;
      },
    },
    {
      title: t("ReferrerEmail"),
      render: (data: IReferralMember) => {
        return data.referralToken.user.email;
      },
    },
    {
      title: t("ReferredName"),
      render: (data: IReferralMember) => {
        return data.referredName;
      },
    },
    {
      title: t("ReferredEmail"),
      render: (data: IReferralMember) => {
        return data.memberEmail;
      },
    },
    {
      title: t("ReferralDate"),
      render: (data: IReferralMember) => {
        return dayjs(data.updatedAt).format("DD/MM/YYYY");
      },
    },
    {
      title: t("CreditsEarned"),
      render: (data: IReferralMember) => {
        return <span className={"credits"}>{data.creditsEarned}</span>;
      },
    },
    {
      title: t("Status"),
      render: (data: IReferralMember) => {
        return (
          <span className={`status ${ReferralMemberEnum[data.status]}`}>
            {t(ReferralMemberEnum[data.status])}
          </span>
        );
      },
    },
  ];

  return (
    <>
      <div className={"page-content referral-page"}>
        <MainTitle text={t("Referral")} />
        <div className={"settings-button-wrapper"}>
          <button className={"settings-button"} onClick={() => setShowReferralSettingsPopup(true)}>
            <svg
              width="20"
              height="20"
              viewBox="0 0 20 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                clipRule="evenodd"
                d="M12.0352 0.808957C12.4052 0.542957 12.9252 0.418957 13.4362 0.605957C14.5226 1.00394 15.5316 1.58762 16.4182 2.33096C16.8352 2.68096 16.9882 3.19196 16.9422 3.64396C16.8672 4.39696 16.9992 5.12396 17.3622 5.74996C17.6822 6.30696 18.1642 6.74696 18.7522 7.05696L18.9772 7.16696C19.3912 7.35396 19.7592 7.74296 19.8522 8.27996C20.0497 9.41812 20.0497 10.5818 19.8522 11.72C19.7692 12.204 19.4622 12.567 19.0992 12.771L18.9772 12.834C18.2872 13.144 17.7232 13.624 17.3612 14.25C16.9992 14.877 16.8672 15.603 16.9422 16.356C16.9872 16.808 16.8352 17.32 16.4182 17.669C15.5316 18.4123 14.5226 18.996 13.4362 19.394C13.204 19.477 12.955 19.5019 12.7109 19.4665C12.4669 19.4312 12.2352 19.3365 12.0362 19.191C11.4202 18.75 10.7232 18.5 10.0002 18.5C9.27717 18.5 8.58017 18.749 7.96517 19.191C7.76602 19.3367 7.53417 19.4314 7.28994 19.4668C7.0457 19.5022 6.79651 19.4772 6.56417 19.394C5.47777 18.996 4.46877 18.4123 3.58217 17.669C3.39439 17.5094 3.24848 17.3064 3.15715 17.0775C3.06582 16.8487 3.03184 16.601 3.05817 16.356C3.13317 15.603 3.00017 14.876 2.63817 14.25C2.30847 13.691 1.8263 13.2377 1.24817 12.943L1.02317 12.833C0.797519 12.733 0.599784 12.5793 0.447252 12.3853C0.29472 12.1913 0.192035 11.9628 0.148173 11.72C-0.0493911 10.5818 -0.0493911 9.41811 0.148173 8.27996C0.231173 7.79596 0.538173 7.43296 0.901173 7.22896L1.02317 7.16696C1.71317 6.85596 2.27717 6.37696 2.63917 5.74996C3.00017 5.12396 3.13317 4.39696 3.05817 3.64396C3.03184 3.39896 3.06582 3.15125 3.15715 2.9224C3.24848 2.69354 3.39439 2.4905 3.58217 2.33096C4.46878 1.58762 5.47778 1.00394 6.56417 0.605957C6.79638 0.52299 7.04536 0.498114 7.2894 0.533499C7.53343 0.568884 7.76509 0.663454 7.96417 0.808957C8.57917 1.25096 9.27617 1.49996 10.0002 1.49996C10.7242 1.49996 11.4202 1.25096 12.0352 0.808957ZM12.9922 2.57796C12.1262 3.14796 11.1052 3.49996 10.0002 3.49996C8.89517 3.49996 7.87417 3.14696 7.00817 2.57796C6.31201 2.85896 5.65892 3.23667 5.06817 3.69996C5.12817 4.73296 4.92317 5.79296 4.37117 6.74996C3.81817 7.70596 3.00317 8.41296 2.07817 8.87796C1.97396 9.62166 1.97396 10.3763 2.07817 11.12C3.00317 11.585 3.81817 12.292 4.37117 13.25C4.92317 14.205 5.12817 15.265 5.06817 16.298C5.65886 16.7616 6.31195 17.1396 7.00817 17.421C7.87417 16.851 8.89517 16.499 10.0002 16.499C11.1052 16.499 12.1262 16.852 12.9922 17.421C13.6883 17.1399 14.3414 16.7622 14.9322 16.299C14.8722 15.265 15.0772 14.205 15.6292 13.249C16.1812 12.292 16.9972 11.585 17.9222 11.119C18.0264 10.3759 18.0264 9.62199 17.9222 8.87896C16.9972 8.41296 16.1822 7.70596 15.6292 6.74896C15.0772 5.79296 14.8722 4.73296 14.9322 3.69896C14.3414 3.23567 13.6883 2.85896 12.9922 2.57796ZM10.0002 5.99996C11.061 5.99996 12.0785 6.42138 12.8286 7.17153C13.5787 7.92168 14.0002 8.93909 14.0002 9.99996C14.0002 11.0608 13.5787 12.0782 12.8286 12.8284C12.0785 13.5785 11.061 14 10.0002 14C8.93931 14 7.92189 13.5785 7.17175 12.8284C6.4216 12.0782 6.00017 11.0608 6.00017 9.99996C6.00017 8.93909 6.4216 7.92168 7.17175 7.17153C7.92189 6.42138 8.93931 5.99996 10.0002 5.99996ZM10.0002 7.99996C9.46974 7.99996 8.96103 8.21067 8.58596 8.58574C8.21089 8.96082 8.00017 9.46952 8.00017 9.99996C8.00017 10.5304 8.21089 11.0391 8.58596 11.4142C8.96103 11.7892 9.46974 12 10.0002 12C10.5306 12 11.0393 11.7892 11.4144 11.4142C11.7895 11.0391 12.0002 10.5304 12.0002 9.99996C12.0002 9.46952 11.7895 8.96082 11.4144 8.58574C11.0393 8.21067 10.5306 7.99996 10.0002 7.99996Z"
                fill="#09244B"
              />
            </svg>
          </button>
        </div>
        <Table
          className={"table"}
          columns={columns}
          rowKey={(record) => record.id}
          dataSource={data.members}
          pagination={{ ...tableParams, total: totalCount }}
          loading={isLoading}
          onChange={handleTableChange}
        />
      </div>
      {showReferralSettingsPopup && referralSettings && (
        <ReferralSettingsPopup
          referralSettings={referralSettings}
          closePopup={closeSettingsPopup}
          getReferralSettings={refetch}
        />
      )}
    </>
  );
};

export default ReferralProgram;
